import { HttpService } from '@/shared/services'
import GetUsersInputDto from './interfaces/GetUsersInputDto'
import UserList from '../models/UserList'
import CreateUserInputDto from './interfaces/CreateUserInputDto'
import UserDetail from '../models/UserDetail'
import CreateManyUsersDto from './interfaces/CreateManyUsersDto'
import type { AxiosResponse } from 'axios'

type CreateManyUsersSuccess = {
  success: true
  message: string
  payload?: any
}

type CreateManyUsersCsvResult = {
  success: false
  fileName: string
  blob: Blob
}

export type CreateManyUsersResult =
  | CreateManyUsersSuccess
  | CreateManyUsersCsvResult
import UpdateUserInputDto from './interfaces/UpdateUserInputDto'
import PaginatedResult from '@/shared/dtos/PaginatedResult'
import User from '../models/User'

const http = new HttpService()

export const updateUser = async (dto: UpdateUserInputDto, username: string) => {
  const response = await http.put('user/' + username, dto)
  return response.data
}

export const createUser = async (dto: CreateUserInputDto) => {
  const response = await http.post('user', dto)
  return response.data
}

const DEFAULT_ERRORS_FILENAME = 'create-users-errors.csv'

const parseFileName = (contentDisposition?: string | null): string => {
  if (!contentDisposition) return DEFAULT_ERRORS_FILENAME

  const filenameMatch = contentDisposition
    .split(';')
    .map((value) => value.trim())
    .find((value) => value.toLowerCase().startsWith('filename='))

  if (!filenameMatch) return DEFAULT_ERRORS_FILENAME

  const fileNameValue = filenameMatch.substring('filename='.length).trim()

  if (!fileNameValue) return DEFAULT_ERRORS_FILENAME

  return fileNameValue.replace(/^"|"$/g, '') || DEFAULT_ERRORS_FILENAME
}

export const createManyUsers = async (
  dto: CreateManyUsersDto
): Promise<CreateManyUsersResult> => {
  const response: AxiosResponse<Blob> = await http.post(
    'user/createManyUsers',
    dto,
    {
      responseType: 'blob',
      headers: {
        Accept: 'text/csv, application/json',
      },
    }
  )

  const contentType = response.headers['content-type'] || ''

  if (contentType.includes('text/csv')) {
    const fileName = parseFileName(response.headers['content-disposition'])

    return {
      success: false,
      fileName,
      blob: response.data,
    }
  }

  const text = await response.data.text()
  const parsed = text ? JSON.parse(text) : undefined

  return {
    success: true,
    message: parsed?.message ?? 'OK',
    payload: parsed,
  }
}

export const enableUser = async (enable: boolean, username: string) => {
  const response = await http.post(`user/${username}/enable`, {
    enable: !enable,
  })

  return response.data
}

export const getUserById = async (username: string) => {
  const response = await http.get<UserDetail>(`user/${username}`)
  return response.data
}

export const deleteUser = async (username: string) => {
  const response = await http.delete('user/' + username)
  return response.data
}

export const resendTemporaryPassword = async (
  username: string,
  userStatus: string
) => {
  const response = await http.post('user/resendTemporaryPassword', {
    username,
    userStatus,
  })

  return response.data
}

export const getUsers = async (inputDto: GetUsersInputDto) => {
  const queryParams = new URLSearchParams()
  if (inputDto.limit !== undefined) {
    queryParams.append('limit', inputDto.limit.toString())
  } else {
    queryParams.append('limit', '10')
  }

  if (inputDto.offset !== undefined)
    queryParams.append('offset', inputDto.offset.toString())
  else queryParams.append('offset', '0')

  if (inputDto.search !== undefined) {
    queryParams.append('search', inputDto.search)
  }

  if (inputDto.companyId) {
    queryParams.append('companyId', inputDto.companyId)
  }

  if (inputDto.payrollId) {
    queryParams.append('payrollId', inputDto.payrollId)
  }

  if (inputDto.sortField) {
    queryParams.append('sortField', inputDto.sortField)
  }

  if (inputDto.sortOrder) {
    queryParams.append('sortOrder', inputDto.sortOrder)
  }

  const response = await http.get<PaginatedResult<User>>(
    `user?${queryParams.toString()}`
  )
  return response.data
}
